import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { TrendingUp, Repeat, BookOpen, Sparkles, Users, Ticket, BarChart3 } from "lucide-react";

function BRL(v) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

function shortDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function relTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `há ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `há ${hrs} h`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `há ${days} d`;
    return d.toLocaleDateString("pt-BR");
}

function KPI({ label, value, hint, icon: Icon, accent, testId }) {
    return (
        <div data-testid={testId} className={`relative rounded-2xl p-6 border border-black/5 overflow-hidden bg-gradient-to-br ${accent}`}>
            <div className="flex items-start justify-between">
                <div className="overline text-[#1A1A1A]/70">{label}</div>
                <Icon className="w-4 h-4 text-[#1A1A1A]/40" />
            </div>
            <div className="font-display font-light text-4xl tracking-tighter mt-3 text-[#1A1A1A]">{value}</div>
            {hint && <div className="font-body text-xs text-[#1A1A1A]/60 mt-1">{hint}</div>}
        </div>
    );
}

function RevenueChart({ daily }) {
    const max = Math.max(1, ...daily.map((d) => d.amount));
    return (
        <div data-testid="revenue-chart" className="bg-white rounded-2xl border border-black/5 p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <BarChart3 className="w-5 h-5 text-[#1A1A1A]" />
                    <h3 className="font-display text-lg tracking-tight">Receita · últimos 30 dias</h3>
                </div>
                <div className="overline text-[#5C5C5C]">Pico: {BRL(max)}</div>
            </div>
            <div className="flex items-end gap-1 h-44">
                {daily.map((d, i) => {
                    const h = max ? Math.max(2, Math.round((d.amount / max) * 100)) : 0;
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                            <div className="w-full flex-1 flex items-end">
                                <div
                                    className="w-full rounded-t-md bg-gradient-to-t from-[#00D4D4] via-[#5B9BD5] to-[#9370DB] transition-all hover:opacity-80"
                                    style={{ height: `${h}%` }}
                                    title={`${shortDate(d.date)}: ${BRL(d.amount)}`}
                                />
                            </div>
                            {d.amount > 0 && (
                                <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block px-2 py-1 rounded-md bg-[#1A1A1A] text-white text-[10px] font-display whitespace-nowrap">
                                    {BRL(d.amount)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="flex justify-between mt-3 text-[10px] font-body text-[#5C5C5C] tracking-wider">
                <span>{shortDate(daily[0]?.date)}</span>
                <span>{shortDate(daily[Math.floor(daily.length / 2)]?.date)}</span>
                <span>{shortDate(daily[daily.length - 1]?.date)}</span>
            </div>
        </div>
    );
}

function RecentTable({ items }) {
    return (
        <div data-testid="recent-transactions" className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            <div className="px-6 py-5 border-b border-black/5">
                <h3 className="font-display text-lg tracking-tight">Transações recentes</h3>
            </div>
            <table className="w-full">
                <thead className="bg-[#FAFAF7] text-left">
                    <tr>
                        <th className="overline text-[#5C5C5C] px-6 py-3">Quando</th>
                        <th className="overline text-[#5C5C5C] px-6 py-3">Cliente</th>
                        <th className="overline text-[#5C5C5C] px-6 py-3">Tipo</th>
                        <th className="overline text-[#5C5C5C] px-6 py-3 text-right">Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((t) => (
                        <tr key={t.session_id} data-testid={`tx-row-${t.session_id}`} className="border-t border-black/5">
                            <td className="px-6 py-3 font-body text-sm text-[#5C5C5C]">{relTime(t.applied_at || t.updated_at || t.created_at)}</td>
                            <td className="px-6 py-3 font-body text-sm">{t.user_email}</td>
                            <td className="px-6 py-3 font-body text-sm">
                                {t.kind === "ebook" ? (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#8a7022] text-xs"><BookOpen className="w-3 h-3" /> E-book</span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#00D4D4]/10 text-[#0a7a7a] text-xs"><Repeat className="w-3 h-3" /> {t.plan_id || "Plano"}</span>
                                )}
                            </td>
                            <td className="px-6 py-3 font-display text-right">{BRL(t.amount)}</td>
                        </tr>
                    ))}
                    {items.length === 0 && (
                        <tr><td colSpan={4} className="px-6 py-12 text-center font-body text-[#5C5C5C]">Nenhuma transação confirmada ainda.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default function AdminRevenue() {
    const [data, setData] = useState(null);

    useEffect(() => { api.get("/admin/revenue").then((r) => setData(r.data)); }, []);

    if (!data) {
        return (
            <AdminLayout eyebrow="Monetização" title="Receita">
                <div className="font-body text-[#5C5C5C]">Calculando métricas...</div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout eyebrow="Monetização" title="Receita & métricas">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI testId="kpi-total" label="Receita total" value={BRL(data.total_revenue)} hint={`${data.total_paid_count} pagamentos confirmados`} icon={TrendingUp} accent="from-[#00D4D4]/15 to-[#9370DB]/5" />
                <KPI testId="kpi-mrr" label="MRR (estimado)" value={BRL(data.mrr)} hint={`${data.active_subscribers} assinantes ativos`} icon={Repeat} accent="from-[#9370DB]/15 to-[#D4AF37]/5" />
                <KPI testId="kpi-30d" label="Últimos 30 dias" value={BRL(data.revenue_30d)} hint="Janela móvel" icon={BarChart3} accent="from-[#D4AF37]/15 to-[#00D4D4]/5" />
                <KPI testId="kpi-avg" label="Ticket médio" value={BRL(data.avg_ticket)} hint="Todas as compras" icon={Ticket} accent="from-[#F2EFEA] to-white" />
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-4">
                <KPI testId="kpi-plans" label="Receita de planos" value={BRL(data.revenue_plans)} icon={Repeat} accent="from-[#00D4D4]/10 to-white" />
                <KPI testId="kpi-ebooks" label="Receita de e-books" value={BRL(data.revenue_ebooks)} icon={BookOpen} accent="from-[#D4AF37]/10 to-white" />
                <KPI testId="kpi-upsell" label="Conversão upsell" value={`${data.upsell_conversion_pct}%`} hint="Quem comprou plano e e-book" icon={Sparkles} accent="from-[#9370DB]/10 to-white" />
            </div>

            <div className="mt-8">
                <RevenueChart daily={data.daily} />
            </div>

            <div className="mt-8">
                <RecentTable items={data.recent} />
            </div>

            <div className="mt-6 text-xs font-body text-[#5C5C5C] flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                MRR estimado normaliza qualquer plano para receita mensal (anual ÷ 12, etc.).
            </div>
        </AdminLayout>
    );
}
